/*
 * Copyright (c) 2022 NEXTWELL INTL SRL. All Rights Reserved.
 * This software is the proprietary information of NEXTWELL INTL SRL.
 * Use is subject to license and non-disclosure terms
 */

import { Component, Input, Output, ViewChild } from '@angular/core';
import { AttachmentVO } from '@well/attachment/domain';
import { forkJoin, Observable, Subject } from 'rxjs';
import { FileRemoveEvent, FileUpload, FileUploadHandlerEvent, FileUploadModule } from 'primeng/fileupload';
import { ButtonModule } from 'primeng/button';
import { TranslateModule } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { AbstractFileStorageService } from '../file-storage/abstract-file-storage.service';

@Component({
  selector: 'well-attachment-upload',
  templateUrl: './attachment-upload.component.html',
  styleUrls: ['./attachment-upload.component.scss'],
  standalone: true,
  imports: [ButtonModule, FileUploadModule, TranslateModule, CommonModule],
})
export class AttachmentUploadComponent {
  @Input() chooseFileButtonLabel = 'Alege fisiere';
  @Input() accept: string | undefined = undefined;
  @Input() multiple = true;
  @Input() maxFileSize = 10000000;
  @ViewChild('fileUploader', { static: true }) private fileUpload: FileUpload | undefined = undefined;
  @Output() uploadCompleted: Subject<AttachmentVO[]> = new Subject();
  @Output() upload: Subject<File[]> = new Subject();

  localFiles: File[] = [];

    constructor(private abstractFileStorageService: AbstractFileStorageService) {
    }

  uploadFiles(localFiles?: File[]) {
    let files: File[] = [];
    if (localFiles && localFiles.length > 0) {
      files = localFiles;
    } else {
      files = this.getSelectedFiles();
    }
    if (files.length === 0) {
      this.notifyFilesUploaded([]);
      return;
    }
    this.uploadMultipleFiles(files);
  }

  clear() {
    this.fileUpload?.clear();
  }

  private notifyFilesUploaded(attachments: AttachmentVO[]) {
    this.uploadCompleted.next(attachments);
  }

  private convertResponse(files: any[]): AttachmentVO[] {
    if (!files) {
      return [];
    }
    return files.map((file) => new AttachmentVO(file.uuid, file.fileName, file.fileType, file.fileSizeKB));
  }

  private getSelectedFiles(): File[] {
    if (!this.fileUpload) {
      return [];
    }
    return this.fileUpload.files;
  }

  onRemove(event: FileRemoveEvent): void {
    this.localFiles.splice(this.localFiles.indexOf(event.file), 1);
    this.upload.next([...this.localFiles]);
  }

  uploadHandler(event: FileUploadHandlerEvent) {
    this.localFiles = event.files as File[];
    this.upload.next(this.localFiles);
    if (this.fileUpload?.mode) {
      this.fileUpload.mode = 'advanced';
    }
  }

  private uploadMultipleFiles(files: File[]) {
    const totalFilesSize: number = files.reduce((sum, item) => sum + item.size, 0);
    if (totalFilesSize > this.maxFileSize) {
      const fileUploadObservables: Observable<any>[] = [];
      for (const file of files) {
        fileUploadObservables.push(this.abstractFileStorageService.uploadMultiple([file]));
      }

      forkJoin(fileUploadObservables).subscribe((uploadedFiles: any[]) => {
        // @ts-ignore
        const savedAttachments = this.convertResponse(uploadedFiles.flat());
        this.notifyFilesUploaded(savedAttachments);
      });
    } else {
      this.abstractFileStorageService.uploadMultiple(files).subscribe(
        (uploadedFiles: any[]) => {
          const savedAttachments = this.convertResponse(uploadedFiles);
          this.notifyFilesUploaded(savedAttachments);
        },
        (error) => console.log(error)
      );
    }
  }
}
